import type { Paginated } from './api';
import type { CustomerCategoryKey } from './customer';
import type { NotificationDeliveryStatus } from './notification-history';

export const COMMUNICATION_CAMPAIGN_KINDS = ['announcement', 'invitation'] as const;
export type CommunicationCampaignKind = (typeof COMMUNICATION_CAMPAIGN_KINDS)[number];

export const COMMUNICATION_CAMPAIGN_STATUSES = [
  'queued',
  'sending',
  'sent',
  'partially_failed',
  'failed',
] as const;
export type CommunicationCampaignStatus = (typeof COMMUNICATION_CAMPAIGN_STATUSES)[number];

/** Groups are ANDed; values inside a group are ORed. An empty object targets all eligible emails. */
export interface CommunicationAudienceFilter {
  categoryKeys?: CustomerCategoryKey[];
  tags?: string[];
  attendedEventIds?: string[];
}

export interface CommunicationAudienceSample {
  customerId: string;
  reference: string;
  name: string;
  email: string;
  categoryKey: CustomerCategoryKey;
}

export interface CommunicationAudiencePreview {
  eligibleCount: number;
  sample: CommunicationAudienceSample[];
}

export interface AdminCommunicationCampaign {
  id: string;
  requestId: string;
  reference: string;
  kind: CommunicationCampaignKind;
  subject: string;
  preheader: string;
  body: string;
  audience: CommunicationAudienceFilter;
  status: CommunicationCampaignStatus;
  recipientCount: number;
  deliveredCount: number;
  failedCount: number;
  pendingCount: number;
  unsubscribeCount: number;
  startedAt: string;
  completedAt: string | null;
  createdByUserId: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminCommunicationCampaignRecipient {
  id: string;
  customerId: string | null;
  customerReference: string;
  recipientName: string;
  recipientEmail: string;
  categoryKey: CustomerCategoryKey;
  deliveryStatus: NotificationDeliveryStatus;
  attemptCount: number;
  maxAttempts: number;
  lastError: string | null;
  providerMessageId: string | null;
  deliveredAt: string | null;
  unsubscribedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PreviewCommunicationAudienceRequest {
  audience: CommunicationAudienceFilter;
}

export interface SendCommunicationCampaignRequest {
  requestId: string;
  kind: CommunicationCampaignKind;
  subject: string;
  preheader?: string;
  body: string;
  audience: CommunicationAudienceFilter;
  expectedRecipientCount: number;
}

export interface CommunicationAudiencePreviewResponse {
  data: CommunicationAudiencePreview;
}

export interface AdminCommunicationCampaignResponse {
  data: AdminCommunicationCampaign;
}

export type AdminCommunicationCampaignListResponse = Paginated<AdminCommunicationCampaign>;
export type AdminCommunicationCampaignRecipientListResponse =
  Paginated<AdminCommunicationCampaignRecipient>;
